import React, { useState } from 'react';
import moment from 'moment';
import { 
	Paper, 
	Typography, 
	Icon, 
	Divider, 
	FormControlLabel, 
	Radio,
	Button,
	Switch,
	Tooltip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Grid
} from '@material-ui/core';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
	root: {
		padding: theme.spacing(3, 2)
	},
	table: {
		'& th': {
			padding: '4px',
			borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
		},
		'& td': {
			paddingBottom: '8px'
		}
	},
	collaboratorsCard: {
		padding: '16px',
		display: 'flex',
		flexDirection: 'column',
		gap: '16px'
	},
	collaboratorHeader: {
		display: 'flex',
		flexDirection: 'column',
		gap: '8px',
		paddingBottom: '16px',
		borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
	},
	tableContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: '12px'
	},
	tableHeader: {
		display: 'grid',
		gridTemplateColumns: '1fr 120px 120px',
		gap: '16px',
		padding: '12px 0',
		borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
		fontWeight: 'bold'
	},
	collaboratorsList: {
		display: 'flex',
		flexDirection: 'column',
		gap: '8px'
	},
	collaboratorItem: {
		display: 'grid',
		gridTemplateColumns: '1fr 120px 120px',
		gap: '16px',
		padding: '12px 0',
		alignItems: 'center'
	},
	collaboratorName: {
		display: 'flex',
		alignItems: 'center'
	},
	collaboratorRole: {
		display: 'flex',
		alignItems: 'center'
	},
	collaboratorStatus: {
		display: 'flex',
		alignItems: 'center'
	},
	ownerTag: {
		backgroundColor: '#e3f2fd',
		color: '#1976d2',
		padding: '4px 8px',
		borderRadius: '4px',
		fontSize: '12px',
		fontWeight: 'bold'
	},
	ldapTag: {
		backgroundColor: '#f3e5f5',
		color: '#7b1fa2',
		padding: '4px 8px',
		borderRadius: '4px',
		fontSize: '12px',
		fontWeight: 'bold'
	},
	emptyState: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: '24px',
		color: 'rgba(0, 0, 0, 0.6)'
	},
	collaboratorFooter: {
		display: 'flex',
		justifyContent: 'flex-end',
		paddingTop: '16px',
		borderTop: '1px solid rgba(0, 0, 0, 0.12)'
	}
}));

const ConfigurationTab = ({ 
	cluster, 
	headNodeSize, 
	region, 
	zone, 
	computeNodeSizes, 
	moabIsIntegrated, 
	role, 
	burstMode, 
	enableBurstRadioButton, 
	handleBurstMode,
	disks,
	licenseType,
	isUserOwner,
	handleEditCollaborators,
	handleDiskModal,
	healthData,
	onToggleDestroyLock
}) => {
	const classes = useStyles();
	const [lockChanging, setLockChanging] = useState(false);
	const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
	const [unlockConfirmName, setUnlockConfirmName] = useState('');

	const hasFlexShapes = (computeNodeSizes) => {
		return computeNodeSizes?.some(size => size.name.includes("Flex"));
	};

	return (
		<div style={{ width: '96%' }} className={clsx(classes.root)}>
			<Grid container spacing={3}>
				{/* Left Column */}
				<Grid item xs={12} md={6}>
					{/* Cluster Information */}
					<Paper className="flex flex-col w-full py-16 rounded-8 border-1" style={{ marginBottom: 24 }}>
					<div className="flex items-center border-b-1 pb-8 mb-16 px-24 w-full">
						<Icon className="text-blue text-24 ml-16 mr-8">info</Icon>
						<Typography className="text-18 font-bold">Cluster Information</Typography>
					</div>
					<div className="flex flex-row w-full px-24 pt-12">
						<div className="flex flex-col w-full">
							<table className="w-full border-collapse mb-40">
								<tbody>
									<tr className="hover:bg-gray-50">
										<td className="py-2 pr-4 w-1/3 align-top">
											<Typography id="cluster_view_info-id" variant="subtitle1" className="font-bold text-right">
												Cluster ID
											</Typography>
										</td>
										<td className="py-2 pl-4 w-2/3">
											<Typography variant="subtitle1" className="text-left">
												{cluster.id}
											</Typography>
										</td>
									</tr>
									<tr className="hover:bg-gray-50">
										<td className="py-2 pr-4 w-1/3 align-top">
											<Typography id="cluster_view_info-head-node-size" variant="subtitle1" className="font-bold text-right">
												Head Node Size
											</Typography>
										</td>
										<td className="py-2 pl-4 w-2/3">
											<Typography variant="subtitle1" className="text-left">
												{headNodeSize}
											</Typography>
										</td>
									</tr>
									{cluster.data.server.volume_size && (
										<tr className="hover:bg-gray-50">
											<td className="py-2 pr-4 w-1/3 align-top">
												<Typography id="cluster_view_info-head-node-disk-size" variant="subtitle1" className="font-bold text-right">
													Head Node Disk Space
												</Typography>
											</td>
											<td className="py-2 pl-4 w-2/3">
												<Typography variant="subtitle1" className="text-left">
													{cluster.data.server.volume_size} GB
												</Typography>
											</td>
										</tr>
									)}
									<tr className="hover:bg-gray-50">
										<td className="py-2 pr-4 w-1/3 align-top">
											<Typography id="cluster_view_info-image-name" variant="subtitle1" className="font-bold text-right">
												Image Name
											</Typography>
										</td>
										<td className="py-2 pl-4 w-2/3">
											<Typography variant="subtitle1" className="text-left">
												{cluster.data.server.image_name}
											</Typography>
										</td>
									</tr>
									<tr className="hover:bg-gray-50">
										<td className="py-2 pr-4 w-1/3 align-top">
											<Typography id="cluster_view_info-createdAt" variant="subtitle1" className="font-bold text-right">
												Created
											</Typography>
										</td>
										<td className="py-2 pl-4 w-2/3">
											<Typography variant="subtitle1" className="text-left">
												{moment(cluster.createdAt).format('LLL')}
											</Typography>
										</td>
									</tr>
									{healthData?.version && (
										<tr className="hover:bg-gray-50">
											<td className="py-2 pr-4 w-1/3 align-top">
												<Typography id="cluster_view_info-api-version" variant="subtitle1" className="font-bold text-right">
													API Version
												</Typography>
											</td>
											<td className="py-2 pl-4 w-2/3">
												<Typography variant="subtitle1" className="text-left">
													{healthData.version}
												</Typography>
											</td>
										</tr>
									)}
									{cluster.data.network.region_id && (
										<tr className="hover:bg-gray-50">
											<td className="py-2 pr-4 w-1/3 align-top">
												<Typography id="cluster_view_info-region" variant="subtitle1" className="font-bold text-right">
													Region
												</Typography>
											</td>
											<td className="py-2 pl-4 w-2/3">
												<Typography variant="subtitle1" className="text-left">
													{region}
												</Typography>
											</td>
										</tr>
									)}
									{cluster.data.network.zone_id && (
										<tr className="hover:bg-gray-50">
											<td className="py-2 pr-4 w-1/3 align-top">
												<Typography id="cluster_view_info-zone" variant="subtitle1" className="font-bold text-right">
													Availability Zone
												</Typography>
											</td>
											<td className="py-2 pl-4 w-2/3">
												<Typography variant="subtitle1" className="text-left">
													{zone}
												</Typography>
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>
				</Paper>
				
				{/* Cluster Protection - Separate Card */}
				{role.includes('admin') && isUserOwner(cluster.owner) && (
					<Paper className="flex flex-col w-full py-16 rounded-8 border-1" style={{ marginBottom: 24 }}>
						<div className="flex items-center border-b-1 pb-8 mb-16 px-20 w-full">
							<Icon className="text-blue text-24 mr-8">lock</Icon>
							<Typography className="text-18 font-bold">Cluster Protection</Typography>
						</div>
						<div className="px-20 pb-16">
							<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
								<div style={{ flex: 1 }}>
									<Typography variant="body1" className="font-bold">
										Lock Cluster from Destroy
									</Typography>
									<Typography variant="caption" color="textSecondary" style={{display: 'block', marginTop: '4px'}}>
										Prevent accidental destruction of this cluster. When enabled, destroy and recycle operations will be disabled.
									</Typography>
								</div>
								<Switch
									checked={cluster.locks?.destroyLock?.active || false}
									onChange={async (e) => {
										if (e.target.checked) {
											// Lock immediately
											setLockChanging(true);
											try {
												await onToggleDestroyLock(cluster.id, true);
											} finally {
												setLockChanging(false);
											}
										} else {
											// Unlock requires confirmation
											setUnlockDialogOpen(true);
										}
									}}
									color="primary"
									disabled={lockChanging || cluster.info.state === 'deploying' || cluster.info.state === 'destroying'}
								/>
							</div>
							{cluster.locks?.destroyLock?.active && cluster.locks?.destroyLock?.timestamp && (
								<Typography variant="caption" color="textSecondary" className="mt-12">
									<Icon className="text-14" style={{verticalAlign: 'middle', marginRight: '4px'}}>schedule</Icon>
									Locked {moment(cluster.locks.destroyLock.timestamp).fromNow()}
									{cluster.locks.destroyLock.lockedBy && ` by ${cluster.locks.destroyLock.lockedBy}`}
								</Typography>
							)}
						</div>
					</Paper>
				)}
				
				{/* Bursting Mode */}
				{moabIsIntegrated && role.includes('admin') && cluster.provider !== 'maas' && (
					<Paper className="flex flex-col w-full py-16 rounded-8 border-1" style={{ marginBottom: 24 }}>
						<div className="flex items-center border-b-1 pb-8 mb-16 px-24 w-full">
							<Icon className="text-blue text-24 ml-16 mr-8">flash_on</Icon>
							<Typography
								id="cluster_view_bursting-service-state"
								className="text-18 font-bold"
							>
								Bursting Mode
							</Typography>
						</div>
						<div className="pb-32">
							<FormControlLabel
								control={
									<Radio
										checked={burstMode === 'off'}
										onChange={() => {
											handleBurstMode(cluster.id, 'off')
										}}
										value="off"
										color="secondary"
										disabled={cluster.info.bursting.type !== 'none' || !enableBurstRadioButton}
									/>
								}
								label="Off"
								labelPlacement="top"
							/>
							<FormControlLabel
								control={
									<Radio
										checked={burstMode === 'min'}
										onChange={() => {
											handleBurstMode(cluster.id, 'min')
										}}
										value="min"
										color="secondary"
										disabled={cluster.info.bursting.type !== 'none' || !enableBurstRadioButton || cluster.info.isMarketplace === true}
									/>
								}
								label="Min"
								labelPlacement="top"
							/>
							<FormControlLabel
								control={
									<Radio
										checked={burstMode === 'max'}
										onChange={() => {
											handleBurstMode(cluster.id, 'max')
										}}
										value="max"
										color="secondary"
										disabled={cluster.info.bursting.type !== 'none' || !enableBurstRadioButton || cluster.info.isMarketplace === true}
									/>
								}
								label="Max"
								labelPlacement="top"
							/>
							<FormControlLabel
								control={
									<Radio
										checked={burstMode === 'all'}
										onChange={() => {
											handleBurstMode(cluster.id, 'all')
										}}
										value="all"
										color="secondary"
										disabled={cluster.info.bursting.type !== 'none' || !enableBurstRadioButton || cluster.info.isMarketplace === true}
									/>
								}
								label="All"
								labelPlacement="top"
							/>
						</div>
						<Typography variant="body2" className="mb-8 text-center px-24">
							Bursting mode enables dynamic scaling of workloads to the cloud, with configurable levels of cloud resource usage based on demand.
						</Typography>
					</Paper>
				)}
				</Grid>
				
				{/* Right Column */}
				<Grid item xs={12} md={6}>
					{/* Cluster Compute Nodes */}
					<Paper className="flex flex-col w-full py-16 rounded-8 border-1" style={{ marginBottom: 24 }}>
					<div className="flex items-center border-b-1 pb-8 mb-16 px-24 w-full">
						<Icon className="text-blue text-24 ml-16 mr-8">memory</Icon>
						<Typography className="text-18 font-bold">Cluster Compute Nodes</Typography>
					</div>
					{computeNodeSizes && computeNodeSizes.length > 0 && hasFlexShapes(computeNodeSizes) ? (
						<>
							<table className={clsx(classes.table, 'w-3/4 text-left ')}>
								<thead>
									<tr>
										<th>Size</th>
										<th>Count</th>
									</tr>
								</thead>
								<tbody>
									{computeNodeSizes
										.filter(size => !size.name.includes("Flex"))
										.map((size, i) => (
											<tr key={i} id={`node-size-${i}`}>
												<td>{size.name}</td>
												<td>{size.count}</td>
											</tr>
										))}
								</tbody>
							</table>
							<hr style={{ borderTop: '1px solid #D0D0D0', width: '90%', margin: '20px auto' }} />

							<table className={clsx(classes.table, 'w-3/4 text-left pb-48')}>
								<thead>
									<tr>
										<th>Size</th>
										<th>OCPUs</th>
										<th>Memory</th>
										<th>Count</th>
									</tr>
								</thead>
								<tbody>
									{computeNodeSizes
										.filter(size => size.name.includes("Flex"))
										.map((size, i) => (														<tr key={i} id={`node-size-${i}`}>
															<td>{size.name}</td>
															<td>{size.ocpus}</td>
															<td>{size.memory}</td>
															<td>{size.count}</td>
														</tr>
										))}
								</tbody>
							</table>
						</>
					) : (
						<table className={clsx(classes.table, 'w-3/4 text-left pb-48')}>
							<thead>
								<tr>
									<th>Size</th>
									<th>Count</th>
								</tr>
							</thead>
							<tbody>
								{computeNodeSizes?.map((size, i) => (
									<tr key={i} id={`node-size-${i}`}>
										<td>{size.name}</td>
										<td>{size.count}</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</Paper>
				
				{/* Collaborators */}
				{role.includes('admin') && (
					<Paper className="flex flex-col w-full py-16 rounded-8 border-1" style={{ marginBottom: 24 }}>
						<div className="flex items-center border-b-1 pb-8 mb-16 px-20 w-full">
							<Icon className="text-blue text-24 mr-8">people</Icon>
							<Typography className="text-18 font-bold">Collaborators</Typography>
						</div>
						
						<div className="px-20">
							<Typography variant="body2" className="text-gray-600 mb-16">
								{cluster.user ? cluster.user.length : 0} users
								{cluster.sharedLdapServers ? ` · ${cluster.sharedLdapServers.length} LDAP group` : ''}
							</Typography>
						
							<div className={classes.tableContainer}>
								<div className={classes.tableHeader}>
									<div className={classes.collaboratorName}>Name</div>
									<div className={classes.collaboratorRole}>Role</div>
									<div className={classes.collaboratorStatus}>Status</div>
								</div>
								
								<div className={classes.collaboratorsList}>
									{cluster.user && cluster.user.map((user, index) => {
										const isOwner = index === 0;
										
										let userRole = "User";
										
										if (isOwner) {
											userRole = "Admin";
										} else if (index % 3 === 0) {
											userRole = "Instructor";
										}
										
										return (
											<div key={`user-${index}`} className={classes.collaboratorItem}>
												<div className={classes.collaboratorName}>
													<Icon className="text-gray-600 mr-8">person</Icon>
													<Typography>
														{`${user.charAt(0).toUpperCase()}${user.substring(1)}`}
													</Typography>
												</div>
												
												<div className={classes.collaboratorRole}>
													{userRole}
												</div>
												
												<div className={classes.collaboratorStatus}>
													{isOwner && (
														<div className={classes.ownerTag}>Owner</div>
													)}
												</div>
											</div>
										);
									})}
									
									{cluster.sharedLdapServers && cluster.sharedLdapServers.map((server, index) => (
										<div key={`ldap-${index}`} className={classes.collaboratorItem}>
											<div className={classes.collaboratorName}>
												<Icon className="text-gray-600 mr-8">group</Icon>
												<Typography>{server.label}</Typography>
											</div>
											
											<div className={classes.collaboratorRole}>
												LDAP
											</div>
											
											<div className={classes.collaboratorStatus}>
												<span className={classes.ldapTag}>Group</span>
											</div>
										</div>
									))}
									
									{(!cluster.user || cluster.user.length === 0) && 
									(!cluster.sharedLdapServers || cluster.sharedLdapServers.length === 0) && (
										<div className={classes.emptyState}>
											<Icon className="text-gray-400 mr-8">info</Icon>
											<Typography variant="body1" color="textSecondary">No collaborators found</Typography>
										</div>
									)}
								</div>
							</div>
						</div>
							
						<div className={classes.collaboratorFooter}>
							{licenseType === 'Site' && role.includes('admin') && isUserOwner(cluster.owner) && (
								<Button
									id="cluster_view_edit-collaborators-btn"
									variant="contained"
									color="secondary"
									onClick={() => handleEditCollaborators()}
									startIcon={<Icon>edit</Icon>}
								>
									Edit Collaborators
								</Button>
							)}
						</div>
					</Paper>
				)}
				
				{/* Disks Information */}
				{role.includes('admin') && (
					<Paper className="flex flex-col w-full py-16 rounded-8 border-1" style={{ marginBottom: 24 }}>
						<div className="flex items-center border-b-1 pb-8 mb-16 px-20 w-full">
							<Icon className="text-blue text-24 mr-8">storage</Icon>
							<Typography className="text-18 font-bold">Disks Information</Typography>
						</div>

						{disks && disks.length > 0 ? (
							<div className="w-full flex justify-center pb-48">
								<table className={clsx(classes.table, 'w-3/4 text-center')}>
									<thead>
										<tr>
											<th>Name</th>
											<th>Type</th>
											<th>Size</th>
										</tr>
									</thead>
									<tbody>
										{disks.map((disk, i) => (
											<tr id={`node-disk-${i}`} key={i}>
												<td>{disk.disk_name}</td>
												<td>{disk.disk_type}</td>
												<td>{disk.disk_volume} GB</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : (
							<div className="w-full flex justify-center items-center pb-48">
								<Typography className="text-16 sm:text-32">None</Typography>
							</div>
						)}
						<div className={classes.collaboratorFooter}>
							<Button
								id="resize-cluster-btn"
								variant="contained"
								color="secondary"
								onClick={() => handleDiskModal()}
								startIcon={<Icon>edit</Icon>}
								disabled={cluster.info.bursting.type === 'service'}
							>
								Manage Disks
							</Button>
						</div>
						{cluster.info.bursting.type === 'service' && (
							<Typography variant="body2" className="text-center w-full">Resize requires Bursting service to be disabled.</Typography>
						)}
					</Paper>
				)}
				</Grid>
			</Grid>
		
		{/* Unlock Confirmation Dialog */}
			<Dialog open={unlockDialogOpen} onClose={() => setUnlockDialogOpen(false)} maxWidth="sm" fullWidth>
			<DialogTitle>
				<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					<Icon style={{ color: '#f57c00' }}>warning</Icon>
					<span>Unlock Cluster Protection</span>
				</div>
			</DialogTitle>
			<DialogContent>
				<Typography variant="body1" paragraph>
					You are about to unlock <strong>{cluster.name}</strong> from destruction protection.
				</Typography>
				<Typography variant="body2" color="textSecondary" paragraph>
					This will allow the cluster to be destroyed or recycled. To confirm, please type the cluster name below:
				</Typography>
				<TextField
					autoFocus
					margin="dense"
					label="Cluster Name"
					type="text"
					fullWidth
					variant="outlined"
					value={unlockConfirmName}
					onChange={(e) => setUnlockConfirmName(e.target.value)}
					placeholder={cluster.name}
					helperText={unlockConfirmName && unlockConfirmName !== cluster.name ? "Cluster name does not match" : ""}
					error={unlockConfirmName && unlockConfirmName !== cluster.name}
				/>
			</DialogContent>
			<DialogActions>
				<Button 
					onClick={() => {
						setUnlockDialogOpen(false);
						setUnlockConfirmName('');
					}} 
					color="default"
				>
					Cancel
				</Button>
				<Button
					onClick={async () => {
						setLockChanging(true);
						setUnlockDialogOpen(false);
						try {
							await onToggleDestroyLock(cluster.id, false);
						} finally {
							setLockChanging(false);
							setUnlockConfirmName('');
						}
					}}
					color="primary"
					variant="contained"
					disabled={unlockConfirmName !== cluster.name}
				>
					Unlock Cluster
				</Button>
			</DialogActions>
		</Dialog>
		</div>
	);
};

export default ConfigurationTab;
